import humps from 'lodash-humps';
import { find, findIndex, forEach, includes, toArray } from 'lodash';
import { EstablishmentSetting } from '@/enums';

const state = {
    currentEstablishment: {},
    settings: {},
    communications: {},
    establishmentModules: [],
    allModules: [],
    dynamicServiceFilters: [],
};

const getters = {
    getEstablishment: state => { return state.currentEstablishment; },
    getEstablishmentId: state => { return state.currentEstablishment.id; },
    getSettings: state => { return state.settings; },
    getCommunications: state => { return state.communications; },
    getEstablishmentModules: state => { return state.establishmentModules; },
    getAllModules: state => { return state.allModules; },
    getDynamicServiceFilters: (state) => { return state.dynamicServiceFilters; },
    getEstablishmentHasModule: (state) => (moduleName) => {
        const index = findIndex(state.allModules, ['name', moduleName]);
        if (index !== -1) {
            const moduleId = state.allModules[index].id;
            return includes(state.establishmentModules, moduleId);
        }
        return false;
    },
    getUniqueModule: state => (moduleName) => {
        const index = findIndex(state.allModules, ['name', moduleName]);
        if (index !== -1) {
            const moduleId = state.allModules[index].id;
            return state.establishmentModules.length === 1 && includes(state.establishmentModules, moduleId);
        }
        return false;
    },
    getEstablishmentSettingById: state => id => {
        return state.settings[id];
    },
    getDisableGoogleAnalyticsSetting: state => state.settings[EstablishmentSetting.DISABLE_GOOGLE_ANALYTICS],
};

const mutations = {
    SET_CURRENT_ESTABLISHMENT(state, payload) {
        state.currentEstablishment = payload;
    },
    SET_SETTINGS(state, payload) {
        state.settings = payload;
    },
    SET_COMMUNICATIONS(state, payload) {
        state.communications = payload;
    },
    SET_ESTABLISHMENT_MODULES(state, payload) {
        state.establishmentModules = payload[EstablishmentSetting.MODULES];
    },
    SET_ALL_MODULES(state, payload) {
        state.allModules = payload;
    },
    SET_DYNAMIC_SERVICE_FILTERS(state, payload) {
        state.dynamicServiceFilters = payload || [];
    },
};

const actions = {
    /**
     * @description Fetch an establishment with is Id
     * @param state
     * @param commit
     * @param dispatch
     * @param establishmentId
     * @returns {Q.Promise<void>}
     */
    async getEstablishment({ commit, getters, rootGetters, dispatch }, establishmentId) {
        try {
            commit('SET_WAITING_FOR_THEME_OVERRIDE', true, { root: true });

            const response = await this._vm.$api.establishment.getWithSettings(establishmentId);

            const themingOverride = find(response.data.settings, ['setting', EstablishmentSetting.THEMING_OVERRIDE]);
            if (themingOverride) {
                const themeColors = themingOverride.value.theme_color_override;
                if (themeColors) {
                    dispatch('overrideCurrentTheme', themeColors, { root: true });
                }
            }

            const product = response.data.head_office.product;
            if (product && product !== 'clicsante') {
                this._vm.$api.setOptions.product(product);
                commit('UPDATE_STYLE', product, { root: true });
                dispatch('changeCurrentTheme', product, { root: true });
            }

            commit('SET_WAITING_FOR_THEME_OVERRIDE', false, { root: true });

            let settings = [];
            forEach(response.data.settings, s => {
                settings[s.setting] = s.value;
            });
            commit('SET_CURRENT_ESTABLISHMENT', humps(response.data));
            commit('SET_SETTINGS', settings);
            commit('SET_ESTABLISHMENT_MODULES', settings);

            if (!getters['getSettings'][EstablishmentSetting.DISABLE_GOOGLE_ANALYTICS]) {
                await dispatch('analytics/useAnalytics', null, { root: true });
            }

            return response.data.id;
        }
        catch (error) {
            commit('SET_WAITING_FOR_THEME_OVERRIDE', false, { root: true });
            throw error;
        }
    },

    /**
     * @description Fetch the configs of communication for an establishment
     * @param commit
     * @param getters
     * @returns {Q.Promise<void>}
     */
    getCommunications({ commit, getters }) {
        const estabId = getters.getEstablishmentId;
        return this._vm.$api.communications.getMessageConfigurationsByEstablishment(estabId)
            .then((response) => {
                commit('SET_COMMUNICATIONS', humps(response.data));
            });
    },

    /**
     * @description Fetch the list of all existing modules from the API
     * @param commit
     * @returns {Q.Promise<void>}
     */
    getAllModules({ commit }) {
        return this._vm.$api.modules.getModules()
            .then(response => {
                const arr = toArray(humps(response.data));
                if (!arr.length) { throw new Error('No modules found'); }
                commit('SET_ALL_MODULES', arr);
            });
    },
    getDynamicServiceFilters({ commit, getters }, { establishmentId }) {
        return this._vm.$api.services.getServiceTemplatesFilters(establishmentId, null, true)
            .then((response) => {
                const data = humps(response.data);
                commit('SET_DYNAMIC_SERVICE_FILTERS', data);
                return getters.getDynamicServiceFilters;
            });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
